import * as XLSX from "xlsx";

/** @typedef {{requiredFields: [], allowedFields: []}} ImportOptions **/

const defaultOptions = {
  requiredFields: [],
  allowedFields: []
};

export class ImportException {
  /** @type {string} **/
  type = undefined;

  /** @type {string} **/
  code = undefined;

  /** @type {object} **/
  context = undefined;

  constructor(code, context = undefined) {
    this.code = code;
    this.context = context;
  }

  /**
   * @return {string}
   */
  getMessage() {
    return this.code;
  }
}

export class ValidationException extends ImportException {
  type = "validation";
}

export class DataImporter {
  /** @type {ImportOptions} **/
  options = undefined;
  /** @type {XLSX.WorkBook} **/
  workBook = undefined;

  /**
   * @param {ImportOptions} options
   **/
  constructor(options = {}) {
    this.options = { ...defaultOptions, ...options };
  }

  async loadFile(file) {
    const fileContents = await file.arrayBuffer();
    this.workBook = XLSX.read(fileContents);
    return this;
  }

  /**
   * @param {XLSX.WorkSheet} workSheet
   * @param {XLSX.Sheet2JSONOpts} opts
   * @return {unknown[]}
   */
  asJson(workSheet = undefined, opts = { defval: "" }) {
    if (workSheet === undefined) {
      workSheet = Object.values(this.workBook.Sheets)[0];
    }
    return XLSX.utils.sheet_to_json(workSheet, opts);
  }

  /**
   * @param {object} data
   * @return {DataImporter}
   */
  validate(data = undefined) {
    if (data === undefined) {
      data = this.asJson();
    }

    if (data.length < 1) {
      throw new ValidationException("NO_ENTITIES");
    }
    if (data.length > 1000) {
      throw new ValidationException("TO_MANY_ENTITIES", {
        max: 1000,
        given: data.length
      });
    }

    let invalidColumns = this.checkColumns(Object.keys(data[0]));
    if (Object.keys(invalidColumns).length > 0) {
      throw new ValidationException("INVALID_COLUMNS", {
        invalidColumns,
        invalidColumnsJoined: Object.keys(invalidColumns).join(", ")
      });
    }

    return this;
  }

  /**
   * @param {array<string>} fields
   * @return {object}
   */
  checkColumns(fields) {
    const result = {};

    // check required
    for (const requiredField of this.options.requiredFields) {
      if (!fields.includes(requiredField)) {
        result[requiredField] = "REQUIRED";
      }
    }

    if (this.options.allowedFields.length < 1) {
      return result;
    }

    // check allowed
    for (const field of fields) {
      if (!this.options.allowedFields.includes(field)) {
        result[field] = "NOT_ALLOWED";
      }
    }

    return result;
  }
}
